import {
    REVIEW_SOURCE_DEFINITIONS,
    type ReviewSourceId,
    type SourceKind,
} from '@flexbyt/shared';

/**
 * The marketing half of a review source.
 *
 * ---------------------------------------------------------------------------
 * What is here and what is not
 * ---------------------------------------------------------------------------
 * The *facts* about a source — how it imports, what URL it needs, its brand
 * colour, whether the deployment can actually import from it — already live in
 * `@flexbyt/shared`. None of them are repeated here. This adds only what a
 * marketing page needs and the product does not: who the page is for, the words
 * they use, and the headline.
 *
 * That split matters. If the day Yelp's import changes, `kind` moves from
 * `connect` to `extension` in the shared definitions, then every marketing page
 * says the new thing immediately — because it reads `kind` rather than
 * restating it. A marketing site that keeps its own copy of how the product
 * works is a marketing site that will eventually describe a product that no
 * longer exists.
 *
 * ---------------------------------------------------------------------------
 * The audiences are the point
 * ---------------------------------------------------------------------------
 * The brief is explicit that these must not be one article with the source name
 * swapped. A hotel manager looking for Tripadvisor reviews and a shop owner
 * looking for Etsy reviews have almost nothing in common, and a page that
 * treats them the same is a page neither recognises themselves in. So each
 * entry carries its own audience and its own reason.
 */

export type SourceContent = {
  id: ReviewSourceId;
  /**
   * The URL segment used by the free tools and the widget pages when they name
   * a source in passing.
   *
   * The source's own acquisition page lives at `/integrations/<id>-reviews` and
   * is built from `content/integrations.ts` — use `integrationPath` for links,
   * so there is one definition of where a source page is.
   */
  slug: string;
  /** How the brand writes its own name — `TripAdvisor`, not `Tripadvisor`. */
  name: string;
  /** Who this page is for, in their own words. */
  audience: string;
  /** The one sentence a visitor should leave with. */
  headline: string;
  /** Why someone with reviews here wants them on their own website. */
  why: string;
};

/**
 * The sources ReviewsJet fetches server-side from a public page.
 *
 * These get the full acquisition experience: a visitor pastes their listing and
 * ReviewsJet goes and reads it. Order is the order they are offered during
 * onboarding, which is roughly the order of how many people ask for them.
 */
export const CONNECT_SOURCES: SourceContent[] = [
  {
    id: 'google',
    slug: 'google',
    name: 'Google',
    audience: 'Local businesses with a Google Business Profile',
    headline: 'Put your Google reviews on your own website',
    why: 'Your Google reviews are doing their work on Google. The people who reach your website have already left that page behind, and the reviews do not come with them.',
  },
  {
    id: 'trustpilot',
    slug: 'trustpilot',
    name: 'Trustpilot',
    audience: 'Companies collecting customer reviews on Trustpilot',
    headline: 'Show your Trustpilot reviews where people decide',
    why: 'A Trustpilot profile is a page somebody has to go and find. The same reviews on your pricing page are in front of the person who is already deciding.',
  },
  {
    id: 'yelp',
    slug: 'yelp',
    name: 'Yelp',
    audience: 'Local businesses and service providers listed on Yelp',
    headline: 'Bring your Yelp reviews onto your website',
    why: 'Yelp sends people to Yelp. Your reviews are the strongest thing about your listing, and they should be on the site you actually control.',
  },
  {
    id: 'tripadvisor',
    slug: 'tripadvisor',
    name: 'Tripadvisor',
    audience: 'Hotels, restaurants, attractions and travel businesses',
    headline: 'Your Tripadvisor reviews, on your own booking page',
    why: 'Guests research on Tripadvisor and book direct when they can. Showing the reviews that convinced them, at the moment they book, closes the gap.',
  },
  {
    id: 'airbnb',
    slug: 'airbnb',
    name: 'Airbnb',
    audience: 'Hosts and property managers with guest reviews',
    headline: 'Show your Airbnb guest reviews on your direct booking site',
    why: 'Years of five-star stays sit inside a listing you do not own. On your own site they are the reason somebody books with you instead of through the platform.',
  },
  {
    id: 'googleplay',
    slug: 'google-play',
    name: 'Google Play',
    audience: 'App makers and SaaS teams with an Android app',
    headline: 'Put your Google Play reviews on your app’s website',
    why: 'Store ratings are the first thing somebody checks before downloading, and your landing page is where they decide whether to go to the store at all.',
  },
  {
    id: 'appstore',
    slug: 'app-store',
    name: 'App Store',
    audience: 'iOS developers and product teams with an iPhone app',
    headline: 'Show your App Store reviews where people decide to download',
    why: 'Your App Store reviews are read inside the App Store. The people on your website, deciding whether to go there, never see them.',
  },
];

/**
 * The marketplaces the Chrome extension imports from.
 *
 * Kept as a separate list rather than a flag on one list, because the brief is
 * firm that these must not be presented as though they work like the five
 * above — and a single array with a `kind` field is exactly how that
 * distinction gets flattened by the first component that maps over it.
 *
 * The difference is real and worth being plain about: these sites cannot be
 * read from a server, so the reviews are collected by the ReviewsJet extension
 * in the user's own browser, while they are signed in and looking at their own
 * page. That is a different promise from "paste a link", and a page that blurs
 * the two produces a visitor who installs nothing and imports nothing.
 */
export const EXTENSION_SOURCES: SourceContent[] = [
  {
    id: 'etsy',
    slug: 'etsy',
    name: 'Etsy',
    audience: 'Etsy sellers with a shop of their own',
    headline: 'Import your Etsy reviews with the ReviewsJet extension',
    why: 'Etsy reviews stay on Etsy. On your own shop they are the difference between a first-time visitor and a first order.',
  },
  {
    id: 'amazon',
    slug: 'amazon',
    name: 'Amazon',
    audience: 'Brands selling on Amazon as well as their own store',
    headline: 'Bring your Amazon product reviews to your own store',
    why: 'The reviews that sell the product on Amazon are the ones missing from the product page you own.',
  },
  {
    id: 'aliexpress',
    slug: 'aliexpress',
    name: 'AliExpress',
    audience: 'Sellers sourcing or selling through AliExpress',
    headline: 'Import AliExpress product reviews with the extension',
    why: 'Product reviews with photos are the proof a new store has none of.',
  },
  {
    id: 'tourradar',
    slug: 'tourradar',
    name: 'TourRadar',
    audience: 'Tour operators listed on TourRadar',
    headline: 'Show your TourRadar reviews on your own tours',
    why: 'Travellers compare operators on reviews. Yours should be on the page where they book, not only on the marketplace that takes a cut.',
  },
];

export const ALL_SOURCE_CONTENT = [...CONNECT_SOURCES, ...EXTENSION_SOURCES];

export function sourceContentBySlug(slug: string): SourceContent | undefined {
  return ALL_SOURCE_CONTENT.find((source) => source.slug === slug);
}

/**
 * How a source imports, straight from the product's own definition.
 *
 * A page asks this rather than assuming, so the wording on the site cannot
 * drift from the mechanism in the dashboard.
 */
export function sourceKind(id: ReviewSourceId): SourceKind {
  return REVIEW_SOURCE_DEFINITIONS[id].kind;
}
